import { useRouter } from 'next/router'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import SliderInput from 'modules/Marketing/SalePriceSlider/SliderInput'
import { interpolate, reverseInterpolate } from 'modules/OldSliders/SalePriceSlider/utils'

import RoundedButton from 'components/RoundedButton'
import Spacer from 'components/Spacer'

import { QueryParamsContext } from 'services/contexts'

import { addCommas } from 'utils/stringFormatting'

const VESTIBULE_WIDTH = 125

interface IPaddingTopProp {
  paddingTop?: number
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const HeaderText = styled.div`
  font-size: 24px;
  font-weight: 400;
  text-align: center;
  width: 100%;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 32px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 42px;
  }
`

const Br = styled.br`
  display: none;
  @media (max-width: ${props => props.theme.metrics.phone}px) {
    display: block;
  }
`

const Bold = styled.span`
  font-weight: 800;
`

const SliderContainer = styled.div<IPaddingTopProp>`
  margin: 0 auto;
  width: 100%;
  max-width: 1000px;
  height: 160px;
  padding-top: ${props => (props.paddingTop ? props.paddingTop : 120)}px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding-top: ${props => (props.paddingTop ? props.paddingTop : 250)}px;
  }
`

const KeepValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 10px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex-direction: row;
    padding-top: 100px;
  }
`

const KeepValueText = styled.span`
  text-align: center;
  font-size: 16px;
  color: ${props => props.theme.colors.black};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    text-align: right;
    font-size: 24px;
  }
`

const StyledSpan = styled.span`
  font-style: normal;
  font-weight: 800;
  font-size: 24px;
  color: ${props => props.theme.colors.green2};

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 42px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`

const MaxWidthContainer = styled.div`
  width: 275px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    width: 375px;
  }
`

const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const H3 = styled.h3`
  color: white;
  font-size: 24px;
  line-height: 32px;
  margin: 0;
`

interface IProps {
  defaultPrice?: number
  defaultSliderText?: boolean
  sliderBubbleText?: string
  sliderOnly?: boolean
  sliderMin?: number
  sliderMax?: number
  paddingTop?: number
  color?: string
  fillHeight?: number
  onChange?: (price: number) => void
}

const SalePriceSlider: FunctionComponent<IProps> = ({
  defaultPrice = 320000,
  defaultSliderText,
  sliderBubbleText,
  sliderOnly,
  sliderMin = 0,
  sliderMax = 150,
  paddingTop,
  color,
  fillHeight,
  onChange,
}) => {
  const theme = useContext(ThemeContext)
  const router = useRouter()

  const defaultTickValue = (price: number) => reverseInterpolate(price)

  // slider state
  const [value, setValue] = useState(defaultTickValue(defaultPrice))

  const handleSliderChange = (_: string, newValue: number) => {
    if (onChange) onChange(interpolate(newValue))

    setValue(newValue)
  }

  useEffect(() => {
    setValue(defaultTickValue(defaultPrice))
  }, [defaultPrice])

  // formulas to compute savings
  const selectedPrice = interpolate(value)
  const brokerSavings = addCommas(Math.floor(selectedPrice * 0.035 - 599).toString())
  const noBrokerSavings = addCommas(Math.floor(selectedPrice * 0.06 - 599).toString())

  // query for appending UTM params
  const { params: query } = useContext(QueryParamsContext)

  const getKeepValueText = (route: string) => {
    switch (route) {
      case 'utah':
        return 'Sell your home and save an extra:'
      default:
        return 'Sell your home with HomeLister and save an extra:'
    }
  }

  const campaignRoute = router.asPath.split('c/').pop() || ''
  const keepValueText = getKeepValueText(campaignRoute)

  return (
    <Container>
      {!sliderOnly && (
        <HeaderText>
          It&apos;s your equity. <Br />
          <Bold>Keep more of it.</Bold>{' '}
        </HeaderText>
      )}
      <SliderContainer paddingTop={paddingTop}>
        {/* Marketing Sale Price Slider */}
        <SliderInput
          max={sliderMax}
          min={sliderMin}
          step={1}
          name="sale-price"
          value={value}
          color={color}
          fillHeight={fillHeight}
          onChange={handleSliderChange}
          metricLabel={sliderBubbleText || "My Home's Value"}
          vestibuleWidth={VESTIBULE_WIDTH}
          interpolateFn={interpolate}
          noVestibules
          skipDebounce
          textColor={defaultSliderText ? theme.colors.white : theme.colors.black}
        />
      </SliderContainer>
      {!sliderOnly && (
        <>
          <KeepValueContainer>
            <KeepValueText>{keepValueText}</KeepValueText>
            <Spacer size={10} />
            <StyledSpan>{` $${brokerSavings} to $${noBrokerSavings}`}</StyledSpan>
          </KeepValueContainer>
          <Spacer
            size={40}
            tabletSize={20}
            responsiveSize={20}
          />
          <ButtonContainer data-testid="getStartedForFreeSalePriceSlider">
            <MaxWidthContainer>
              <RoundedButton
                text="Get started for free"
                href={{ pathname: '/listing/create', query: query as { [key: string]: string } }}
                color={theme.colors.white}
                background={theme.colors.blue4}
                hoverBackground={theme.colors.blue9}
                fontSize={16}
                radius={100}
                height={50}
                fullWidth
              />
            </MaxWidthContainer>
          </ButtonContainer>
        </>
      )}
      {defaultSliderText && (
        <BottomContainer>
          <H3>Keep an extra</H3>
          <H3>
            ${brokerSavings} to ${noBrokerSavings}!
          </H3>
          <Spacer size={25} />
          <RoundedButton
            text="Start Saving Now"
            href={{
              pathname: '/listing/create',
              query: query as { [key: string]: string },
            }}
            background={theme.colors.yellow}
            radius={20}
            width={234}
          />
        </BottomContainer>
      )}
    </Container>
  )
}

export default SalePriceSlider
